import {
  faMoon, faSun,
} from '@fortawesome/pro-solid-svg-icons';
import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { getMapOwnerUserId } from '~/map/map.helpers';
import { useSharedPresentationalMapsSearchFilterDropdown } from '~/map/settings/accordion/sharedPresentationalMaps/useSharedPresentationalMapSettings/useSharedPresentationalMapsSearchFilterDropdown';
import { useSharedPresentationalMapsToolsMenuDropdown } from '~/map/settings/accordion/sharedPresentationalMaps/useSharedPresentationalMapSettings/useSharedPresentationalMapsToolsMenuDropdown';
import { useModal } from '~/modal/useModal.hook';
import { useMapInfoDataSelector } from '~/store/mapInfo/mapInfo.selectors';
import {
  publicMapSettingsSetTheme,
  publicMapSettingsUpdate,
} from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.actionCreators';
import { usePublicMapSettingsSelector } from '~/store/mapSettings/publicMapSettings/mapSettingsPublicMapSettings.selectors';
import {
  useUserIdSelector, useUserLimitsSelector,
} from '~/store/userData/userData.selectors';
import { SharedPresentationalMapsBooleanToggleSettingsName } from '../sharedPresentationalMaps.enums';
import { type SharedPresentationalMapsStructureProps } from '../sharedPresentationalMaps.types';
import { usePresentationalDataExport } from '../usePresentationalDataExport.hook';
import { useSharedPresentationalMapsMapKeyDropdown } from './useSharedPresentationalMapsMapKeyDropdown';

export const useSharedPresentationalMapsSettings = (): SharedPresentationalMapsStructureProps => {
  const mapInfo = useMapInfoDataSelector();
  const userId = useUserIdSelector();
  const publicMapSettings = usePublicMapSettingsSelector();
  const userLimits = useUserLimitsSelector();
  const searchBarFilterDropdown = useSharedPresentationalMapsSearchFilterDropdown();
  const mapToolsMenuDropdown = useSharedPresentationalMapsToolsMenuDropdown();
  const mapKeyDropdown = useSharedPresentationalMapsMapKeyDropdown();
  const dispatch = useDispatch();
  const { openModal: openRestrictZoomLevelsModal } = useModal('RestrictZoomLevels');
  const { openModal: openEnableDisableColumnsOnPublicMapModal } = useModal('PublicMapColumnsDisplaySettings');
  const { openModal: openMapMarkerColumnsDisplaySettingsModal } = useModal('MapMarkerColumnsDisplaySettings');
  const { openModal: openExportDataFunctionalitiesColumnsDisplaySettingsModal } = useModal('ExportDataFunctionalitiesColumnsDisplaySettings');
  const { openModal: openEmbedMapModal } = useModal('EmbedMap');
  const { openContactUsForAuthorization, openSelectToolsForAllowExport } = usePresentationalDataExport();

  const isCurrentUserOwner = mapInfo && getMapOwnerUserId(mapInfo) === userId;

  const onToggleThemeStyle = useCallback(() => {
    const currentTheme = publicMapSettings.theme;
    dispatch(publicMapSettingsSetTheme(currentTheme === 'dark' ? 'light' : 'dark'));
  }, [dispatch, publicMapSettings.theme]);

  const onTogglePublicSettings = useCallback((name: SharedPresentationalMapsBooleanToggleSettingsName) =>
    () => dispatch(publicMapSettingsUpdate(name, !publicMapSettings[name]))
  , [dispatch, publicMapSettings]);

  const onRestrictZoomLevelsClick = useCallback(() => {
    openRestrictZoomLevelsModal();
  }, [openRestrictZoomLevelsModal]);

  const openShowHideDataOnMarker = useCallback(() => {
    openMapMarkerColumnsDisplaySettingsModal();
  }, [openMapMarkerColumnsDisplaySettingsModal]);

  const openShowHideDataFromExport = useCallback(() => {
    openExportDataFunctionalitiesColumnsDisplaySettingsModal();
  }, [openExportDataFunctionalitiesColumnsDisplaySettingsModal]);

  const openShowHideFilterColumns = useCallback(() => {
    openEnableDisableColumnsOnPublicMapModal();
  }, [openEnableDisableColumnsOnPublicMapModal]);

  const buttons = useMemo(() => ({
    restrictZoomLevels: {
      onClick: onRestrictZoomLevelsClick,
    },
    showHideDataOnMarker: {
      onClick: openShowHideDataOnMarker,
    },
    showHideDataFromExport: {
      onClick: openShowHideDataFromExport,
    },
    showHideFilterColumns: {
      onClick: openShowHideFilterColumns,
    },
    embedMap: {
      onClick: openEmbedMapModal,
    },
    presentationalDataExportAuthorize: {
      onClick: isCurrentUserOwner && !userLimits.canExportSharedMapsData
        ? openContactUsForAuthorization : undefined,
    },
    presentationalDataExportAllowTools: {
      onClick: isCurrentUserOwner && userLimits.canExportSharedMapsData
        ? openSelectToolsForAllowExport : undefined,
    },
  }), [isCurrentUserOwner, onRestrictZoomLevelsClick, openContactUsForAuthorization, openEmbedMapModal,
    openSelectToolsForAllowExport, openShowHideDataFromExport, openShowHideDataOnMarker,
    openShowHideFilterColumns, userLimits.canExportSharedMapsData,
  ]);

  const dropdowns = useMemo(() => ({
    defaultOpenMapToolPresentationalMap: mapToolsMenuDropdown,
    searchBarFilterDefaultOption: searchBarFilterDropdown,
    defaultOpenMapKey: mapKeyDropdown,
  }), [mapToolsMenuDropdown, searchBarFilterDropdown, mapKeyDropdown]);

  const toggles = useMemo(() => ({
    displayTitleAndDescription: {
      isOn: publicMapSettings.displayTitleAndDescription,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.displayTitleAndDescription),
    },
    displaySearchBox: {
      isOn: publicMapSettings.displaySearchBox,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.displaySearchBox),
    },
    distanceCalculator: {
      isOn: publicMapSettings.distanceCalculator,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.distanceCalculator),
    },
    lassoTool: {
      isOn: publicMapSettings.lassoTool,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.lassoTool),
    },
    showMyLocation: {
      isOn: publicMapSettings.showMyLocation,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.showMyLocation),
    },
    showMyLocationDefault: {
      isOn: publicMapSettings.showMyLocationDefault,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.showMyLocationDefault),
    },
    restrictMapPanning: {
      isOn: publicMapSettings.restrictMapPanning,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.restrictMapPanning),
    },
    allowStreetView: {
      isOn: publicMapSettings.allowStreetView,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.allowStreetView),
    },
    allowSatelliteView: {
      isOn: publicMapSettings.allowSatelliteView,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.allowSatelliteView),
    },
    displayLegendOnly: {
      isOn: publicMapSettings.displayLegendOnly,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.displayLegendOnly),
    },
    boundaryTool: {
      isOn: publicMapSettings.boundaryTool,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.boundaryTool),
    },
    filterMenusOpen: {
      isOn: publicMapSettings.filterMenusOpen,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.filterMenusOpen),
    },
    filterTool: {
      isOn: publicMapSettings.filterTool,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.filterTool),
    },
    groupingTool: {
      isOn: publicMapSettings.groupingTool,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.groupingTool),
    },
    heatMappingTool: {
      isOn: publicMapSettings.heatMappingTool,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.heatMappingTool),
    },
    legendReplacesGrouping: {
      isOn: publicMapSettings.legendReplacesGrouping,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.legendReplacesGrouping),
    },
    locationFinder: {
      isOn: publicMapSettings.locationFinder,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.locationFinder),
    },
    locationFinderStartsOpen: {
      isOn: publicMapSettings.locationFinderStartsOpen,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.locationFinderStartsOpen),
    },
    mapKey: {
      isOn: publicMapSettings.mapKey,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.mapKey),
    },
    mapKeyOpen: {
      isOn: publicMapSettings.mapKeyOpen,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.mapKeyOpen),
    },
    /*
      We have decided to disable Map Image Export for presentational maps for beta release
      TODO: remove this some time after release of we decide to never use this
    */
    mapImageExport: {
      isOn: publicMapSettings.mapImageExport,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.mapImageExport),
    },
    mapToolsMenuOpen: {
      isOn: publicMapSettings.mapToolsMenuOpen,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.mapToolsMenuOpen),
    },
    mapZoom: {
      isOn: publicMapSettings.mapZoom,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.mapZoom),
    },
    radiusProximity: {
      isOn: publicMapSettings.radiusProximity,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.radiusProximity),
    },
    routingDirections: {
      isOn: publicMapSettings.routingDirections,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.routingDirections),
    },
    searchBarAlwaysVisible: {
      isOn: publicMapSettings.searchBarAlwaysVisible,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.searchBarAlwaysVisible),
    },
    showSearchFilter: {
      isOn: publicMapSettings.showSearchFilter,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.showSearchFilter),
    },
    markersVisibilityButton: {
      isOn: publicMapSettings.markersVisibilityButton,
      onToggle: onTogglePublicSettings(SharedPresentationalMapsBooleanToggleSettingsName.markersVisibilityButton),
    },
    themeStyle: {
      isOn: publicMapSettings.theme === 'dark',
      onToggle: onToggleThemeStyle,
      offIcon: faSun,
      onIcon: faMoon,
    },
  }), [onTogglePublicSettings, publicMapSettings.allowSatelliteView, publicMapSettings.allowStreetView,
    publicMapSettings.boundaryTool, publicMapSettings.displayLegendOnly, publicMapSettings.showSearchFilter,
    publicMapSettings.displaySearchBox, publicMapSettings.displayTitleAndDescription, publicMapSettings.distanceCalculator,
    publicMapSettings.filterMenusOpen, publicMapSettings.filterTool, publicMapSettings.groupingTool,
    publicMapSettings.heatMappingTool, publicMapSettings.lassoTool, publicMapSettings.legendReplacesGrouping,
    publicMapSettings.locationFinder, publicMapSettings.locationFinderStartsOpen, publicMapSettings.mapKey,
    publicMapSettings.mapKeyOpen, publicMapSettings.mapImageExport, publicMapSettings.mapToolsMenuOpen,
    publicMapSettings.mapZoom, publicMapSettings.markersVisibilityButton, publicMapSettings.radiusProximity,
    publicMapSettings.restrictMapPanning, publicMapSettings.routingDirections, publicMapSettings.searchBarAlwaysVisible,
    publicMapSettings.showMyLocation, publicMapSettings.showMyLocationDefault, publicMapSettings.theme,
    onToggleThemeStyle,
  ]);

  return useMemo(() => ({
    buttons,
    dropdowns,
    toggles,
  }), [buttons, dropdowns, toggles]);
};
